import { MarketplaceVendorsCategoryString } from '@types';

interface MarketplaceVendorCategoryItem {
  title: string;
  // description: string;
  description: React.ReactNode;
  tag: 'All Tools' | MarketplaceVendorsCategoryString;
}

export const vendorCategoryList: MarketplaceVendorCategoryItem[] = [
  {
    title: 'Recommended Startup Stacks',
    description: (
      <>
        <strong>Lean SaaS (pre-seed)</strong>
        <ul className='list-disc ml-6 mt-2 mb-2'>
          <li>
            <strong>Accounting:</strong> QBO (+ Accruer when deferrals start)
          </li>
          <li>
            <strong>Banking:</strong> Mercury
          </li>
          <li>
            <strong>Billing:</strong> Stripe Billing (+ Synder/Booker)
          </li>
          <li>
            <strong>Payroll:</strong> Gusto
          </li>
          <li>
            <strong>Expenses/AP:</strong> Ramp; add Bill.com once AP &gt;15
            bills/month
          </li>
        </ul>

        <strong>Scaling SaaS (seed → A, ~25–50 FTE)</strong>
        <ul className='list-disc ml-6 mt-2 mb-2'>
          <li>
            <strong>Accounting:</strong> QBO + Accruer + Bill.com + Tabs
          </li>
          <li>
            <strong>Banking/Cards:</strong> Mercury + Ramp (virtual cards per
            vendor)
          </li>
          <li>
            <strong>Billing:</strong> Stripe Billing (usage), Synder
          </li>
          <li>
            <strong>Payroll/HR:</strong> Gusto or a PEO as benefits needs grow
            OR employees in multiple states
          </li>
          <li>
            <strong>Optional:</strong> SpendBase for renewals
          </li>
        </ul>

        <strong>Distributed team with international hires</strong>
        <ul className='list-disc ml-6 mt-2 mb-2'>
          <li>
            <strong>Accounting:</strong> QBO + Accruer
          </li>
          <li>
            <strong>Banking/Cards:</strong> Mercury + Brex or Ramp
          </li>
          <li>
            <strong>Billing:</strong> Stripe + Synder
          </li>
          <li>
            <strong>Payroll/HR:</strong> Gusto or PEO; add Deel for global
            EOR/contractors
          </li>
          <li>
            <strong>AP/AR:</strong> Bill.com + Tabs
          </li>
        </ul>
      </>
    ),
    tag: 'All Tools',
  },
  {
    title: 'Accounting',
    description: (
      <>
        <strong>
          Default (Pre-seed to early Series A): <i>QuickBooks Online (QBO)</i>
        </strong>
        <ul className='list-disc ml-6 mt-2 mb-2'>
          <li>
            <strong>Why:</strong> widely used, easy to connect to other tools,
            fast monthly close, clear audit trail.
          </li>
          <li>
            <strong>Best for:</strong> &lt;$10–15M ARR, simple revenue needs,
            one company (or simple structure), cash or light accrual accounting.
          </li>
          <li>
            <strong>Not ideal for:</strong> complex multi-entity setups, heavy
            inventory/manufacturing, or advanced revenue recognition.
          </li>
          <li>
            <strong>Warning:</strong> QBO is great for accounting and invoicing,
            but we do not recommend their payroll solution.
          </li>
        </ul>
        <strong>
          Add-ons we like with <i>QBO:</i>
        </strong>
        <ul className='list-disc ml-6 mt-2 mb-2'>
          <li>
            <strong>
              <i>Accruer:</i>
            </strong>{' '}
            automates revenue deferrals, prepaids, and fixed assets. OpStart
            will set this one up for you.
          </li>
          <li>
            <strong>
              <i>Booker:</i>
            </strong>{' '}
            Automates journal entries for large volume of data such as Stripe
            transactions and/or payroll entries that can not be automated
            through payroll provider.
          </li>
          <li>
            <strong>
              <i>Synder:</i>
            </strong>{' '}
            clean e-commerce/processor syncs (Stripe, Shopify, Amazon, PayPal).
          </li>
          <li>
            <strong>
              <i>Ramp:</i>
            </strong>{' '}
            vendor payments and perks/rewards for spending.
          </li>
        </ul>
        <strong>
          When to step up (later Series A+): <i>Rillet</i> (<i>NetSuite</i> if
          you have inventory)
        </strong>
        <ul className='list-disc ml-6 mt-2 mb-2'>
          <li>
            <strong>Why:</strong> best for SaaS companies with no inventory
            scaling quickly; automates part of the accounting process.
          </li>
          <li>
            If you have inventory, we recommend stepping up to{' '}
            <strong>
              <i>NetSuite</i>
            </strong>{' '}
            or using it from day one.
          </li>
          <li>
            <strong>Why:</strong> best for multi-entity consolidation, advanced
            revenue recognition rules, complex approvals. Plan for a 3–6 month
            implementation.
          </li>
        </ul>
        <strong>How we implement:</strong>
        <ul className='list-disc ml-6 mt-2 mb-2'>
          <li>
            Standardize your chart of accounts and departments before connecting
            apps.
          </li>
          <li>
            Map payroll system to chart of accounts and automatically sync
            entries to accounting system
          </li>
          <li>
            Turn on bank rules, setup recurring transactions, prepare a monthly
            close checklist, and require attachments on bills and journal
            entries.
          </li>
          <li>
            Add Accruer once you have recurring deferrals or fixed assets.
          </li>
        </ul>
      </>
    ),
    tag: MarketplaceVendorsCategoryString.Accounting,
  },
  {
    title: 'Banking Recommendations',
    description: (
      <>
        <span>
          <strong>
            Indifferent on which primary bank to use: <i>Mercury</i>, <i>Rho</i>
            , and traditional banks are all great options.
          </strong>
        </span>
        <br />
        <div className='mt-2'>
          <strong>
            Default (Pre-seed → Series A): <i>Rho + Ramp/Brex</i>
          </strong>
        </div>
        <ul className='list-disc ml-6 mt-2 mb-2'>
          <li>
            <strong>Why:</strong> startup-friendly UX, FDIC sweep, quick account
            setup, easy payment rails, simple user controls.
          </li>
          <li>
            <strong>
              <i>Ramp/Brex</i> (as bank adjunct):
            </strong>{' '}
            Cards + cash management; we often{' '}
            <strong>keep primary ops banking on Mercury</strong> and layer cards
            from Brex/Ramp for spend controls/rewards.
          </li>
          <li>
            <strong>When to choose:</strong> single entity, U.S. focus, basic
            treasury needs.
          </li>
        </ul>
        <strong>Alternatives</strong>
        <ul className='list-disc ml-6 mt-2 mb-2'>
          <li>
            <strong>
              <i>Mercury</i>:
            </strong>{' '}
            banking + AP/expense in one; good if you want tighter native
            controls/approvals without stitching tools.
          </li>
          <li>
            <strong>Traditional bank (later):</strong> If you need specialized
            products (asset-based lines, SVB-style venture debt).
          </li>
        </ul>
        <strong>Controls to enable day 1:</strong>
        <ul className='list-disc ml-6 mt-2 mb-2'>
          <li>
            Dual approval on wires/ACH, user roles per function, vendor
            whitelist, monthly positive pay (if available)
          </li>
        </ul>
        <strong>Pro Tips:</strong>
        <ul className='list-disc ml-6 mt-2 mb-2'>
          <li>
            We recommend setting up 3 bank accounts:
            <ul className='list-disc ml-6 mt-2 mb-2'>
              <li>
                <strong>1. Primary checking account</strong> that covers all
                operating expenses.
              </li>
              <li>
                <strong>2. Money-market account</strong> to earn interest on
                excess cash; can use the same bank as your primary checking
                account.
                <ul className='list-disc ml-6 mt-2 mb-2'>
                  <li>
                    Set up automated rebalancing transfers at month-end to
                    ensure you begin each month with plenty of cash.
                  </li>
                </ul>
              </li>
              <li>
                <strong>3. Secondary account</strong> with a different bank in
                case of emergencies like the recent SVB crisis.
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    tag: MarketplaceVendorsCategoryString.Banking,
  },
  {
    title: 'Billing & Invoicing Recommendations',
    description: (
      <>
        <strong>Pick based on your revenue model.</strong>
        <ul className='list-disc ml-6 mt-2 mb-2'>
          <li>
            <strong>
              Self-serve, low-dollar, or consumer SaaS subscriptions:
            </strong>{' '}
            Stripe
            <ul className='list-disc ml-6 mt-2 mb-2'>
              <li>
                <strong>Why:</strong> quick to launch, global payments,
                metered/tiers, built-in dunning.
              </li>
              <li>
                <strong>Tip:</strong> connect to QBO via{' '}
                <strong>
                  <i>Stripe</i>
                </strong>{' '}
                sync, Booker or Synder, and align your product catalog with your
                chart of accounts.
              </li>
            </ul>
          </li>
          <li>
            <strong>
              B2B complex revenue: <i>Tabs</i> or <i>Stripe</i> (<i>QBO</i>{' '}
              invoicing is fine for very simple needs)
            </strong>
            <ul className='list-disc ml-6 mt-2 mb-2'>
              <li>
                Choose Tabs if you have over $1M ARR and complexities in your
                pricing model.
              </li>
              <li>
                <strong>Why:</strong> better handling of milestones, products,
                and tiers.
              </li>
            </ul>
          </li>
          <li>
            <strong>
              High-volume marketplace or e-commerce: <i>Shopify/Stripe</i> (+{' '}
              <i>Synder</i> to accounting system)
            </strong>
            <ul className='list-disc ml-6 mt-2 mb-2'>
              <li>
                <strong>Why:</strong> reliable payouts and robust APIs, with
                clean reconciliation.
              </li>
            </ul>
          </li>
          <li>
            <strong>Pro Tips:</strong>
            <ul className='list-disc ml-6 mt-2 mb-2'>
              <li>
                Closely monitor % based payment fees as you scale. Platforms
                like{' '}
                <strong>
                  <i>Stripe</i>
                </strong>{' '}
                have no platform fee and can be great to get started but may
                cost you once revenue starts to take off. Consider switching to
                a platform like Tabs which has a higher platform fee but a
                smaller % fee on each transaction.
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    tag: MarketplaceVendorsCategoryString.BillingInvoicing,
  },
  {
    title: 'Payroll Recommendations',
    description: (
      <>
        <strong>
          <i>JustWorks</i> is our primary recommendation, but <i>Gusto</i> and{' '}
          <i>Deel</i> are good alternatives.
        </strong>
        <ul className='list-disc ml-6 mt-2 mb-2'>
          {/* <li>
            <strong>1–20 employees, U.S. only, simple HR:</strong> Gusto
            <ul className='list-disc ml-6 mt-2 mb-2'>
              <li>
                <strong>Why:</strong> easy onboarding, benefits marketplace,
                time tools, clean QBO sync.
              </li>
            </ul>
          </li> */}
          <li>
            <strong>
              If you have 2+ employees and are willing to spend a little more
              per employee: <i>JustWorks</i>
            </strong>
            <ul className='list-disc ml-6 mt-2 mb-2'>
              <li>
                <strong>Why:</strong> simplifies insurance and compliance with
                employees across multiple states. Switching cost is high for
                payroll, so it’s best to pick the one you can stay with long
                term.
              </li>
            </ul>
          </li>

          <li>
            <strong>Alternatives and when: </strong>
            <ul className='list-disc ml-6 mt-2 mb-2'>
              <li>
                <strong>
                  1–20 employees, U.S. only, simple HR: <i>Gusto</i>
                </strong>
                <ul className='list-disc ml-6 mt-2 mb-2'>
                  <li>
                    <strong>Why:</strong> easy onboarding, benefits marketplace,
                    time tools, clean QBO sync. Most cost-effective for
                    companies with employees in a single state or a small number
                    of states.
                  </li>
                  <li>
                    <strong>
                      Why choose <i>JustWorks</i> over <i>Gusto</i>:{' '}
                    </strong>{' '}
                    Gusto gets more expensive as more states are added and our
                    team or an outside firm has to register in each state. While
                    JustWorks pricing remains the same and registrations are
                    included.
                  </li>
                </ul>
              </li>
              <li>
                <li>
                  <strong>
                    Hiring globally (contractors or employees): <i>Deel</i>
                  </strong>
                  <ul className='list-disc ml-6 mt-2 mb-2'>
                    <li>
                      <strong>Why:</strong> localized compliance, payroll, and
                      benefits through EOR or contractor management.
                    </li>
                  </ul>
                </li>
              </li>
              <li>
                <li>
                  <strong>
                    Hiring both internationally and domestic: <i>JustWorks</i>
                  </strong>
                  <ul className='list-disc ml-6 mt-2 mb-2'>
                    <li>
                      <strong>Why:</strong> provides international payroll and
                      EOR services, but does a better job of handling domestic
                      payroll than Deel.
                    </li>
                  </ul>
                </li>
              </li>
            </ul>
          </li>
        </ul>
        <strong>Integration tips:</strong>
        <ul className='list-disc ml-6 mt-2 mb-2'>
          <li>
            Set departments/locations before your first payroll and map them to
            QBO accounts for a clean P&L.
          </li>
          <li>
            Define a reimbursement policy and route all reimbursements through
            your expense tool.
          </li>
        </ul>
      </>
    ),
    tag: MarketplaceVendorsCategoryString.Payroll,
  },
  {
    title: 'Expense Management Recommendations',
    description: (
      <>
        <p className='mt-2 mb-2'>
          For expense management systems we recommend{' '}
          <strong>
            <i>Ramp</i>
          </strong>
          ,
          <strong>
            <i>Brex</i>
          </strong>
          , or{' '}
          <strong>
            <i>Rho</i>
          </strong>
          . When making a decision, you should research all three options and
          pick the one that offers the perks/rewards you prefer.
        </p>
        <strong>
          Default (most startups): <i>Ramp</i>
        </strong>
        <ul className='list-disc ml-6 mt-2 mb-2'>
          <li>
            <strong>Why:</strong> Strong controls, real-time receipts, vendor
            insights/savings, smooth QBO sync, solid bill pay.
          </li>
          <li>
            <strong>
              When to choose <i>Brex</i>:
            </strong>{' '}
            If you value ecosystem perks/rewards, or have significant ad spend
            and travel benefits leverage.
          </li>
          <li>
            <strong>
              When to choose <i>Rho</i>:
            </strong>{' '}
            If you want banking + AP + cards in one platform with centralized
            approvals.
          </li>
        </ul>
        <strong>Baseline policies to configure:</strong>
        <ul className='list-disc ml-6 mt-2 mb-2'>
          <li>
            Virtual cards per vendor, monthly spending limits, automatic receipt
            capture, category locks (e.g., no gift cards), and manager approvals
            `{'>'}`$X.
          </li>
          <li>
            Route reimbursements through the expense tool; forbid cash
            reimbursements.
          </li>
        </ul>
      </>
    ),
    tag: MarketplaceVendorsCategoryString.ExpenseManagement,
  },
  {
    title: 'Misc Recommendations',
    description: (
      <ul className='list-disc ml-6 mt-2 mb-2'>
        <li>
          <strong>
            <i>LevelUp</i>
          </strong>{' '}
          — Gives early teams on-demand recruiting and people-ops help so you
          can hire faster, onboard consistently, and build basic HR processes
          without adding full-time headcount.
        </li>
        <li>
          <strong>
            <i>Simple Closure</i>
          </strong>{' '}
          — Handles company wind-down end to end—closing accounts, notifying
          agencies, and filing final taxes—so founders avoid legal/tax pitfalls
          and wrap up cleanly.
        </li>
        <li>
          <strong>
            <i>Vouch</i>
          </strong>{' '}
          — Startup-specific insurance with fast quoting and the right coverages
          (GL, Cyber, D&O, EPL). It simplifies risk management and scales as
          your company grows.
        </li>
        <li>
          <strong>
            <i>TaxWire</i>
          </strong>{' '}
          — Automates sales-tax calculation and filings across states (and
          internationally), reducing manual work and audit risk as you expand
          into new markets.
        </li>
        <li>
          <strong>
            <i>Carta</i>
          </strong>{' '}
          — Software platform that helps companies manage their equity,
          fundraising and finances.
        </li>
        <li>
          <strong>
            <i>Eqvista</i>
          </strong>{' '}
          — Lower-cost alternative to Carta, helping companies manage their
          equity and offers 409a valuation services.
        </li>
      </ul>
    ),
    tag: MarketplaceVendorsCategoryString.Misc,
  },
];
